/// <reference path="../svg.d.ts" />
import React from 'react';
import {
  HandCoins,
  Copy,
  Wallet,
  CreditCard,
  CircleCheck,
  Landmark,
  Banknote,
  Coins,
} from 'lucide-react';
import { getIconProps, IconProps } from '../utils';

export const HandCoinsIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <HandCoins {...iconProps} />;
};

export const CopyIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <Copy {...iconProps} />;
};

export const WalletIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <Wallet {...iconProps} />;
};

export const PayIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <CreditCard {...iconProps} />;
};

export const BankIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <Landmark {...iconProps} />;
};

/**
 * Cash payment icon — used for the cash payment-method affordance
 * (e.g. cash-on-delivery toggle in the order price drawer). `Coins`
 * reads as "physical money" without colliding with the more specific
 * `Banknote` glyph already used by {@link TransactionIcon}.
 */
export const CashIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <Coins {...iconProps} />;
};

/** Paid on time — green check circle (same as InvoiceStatusPaidIcon). */
export const PayOnTimeIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <CircleCheck {...iconProps} />;
};

/** Paid early — same green check circle (positive outcome). */
export const PayEarlyIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <CircleCheck {...iconProps} />;
};

/**
 * BalanceIcon — The green circle with a bill/banknote illustration
 * used on the Balance Card. A custom SVG (not from lucide).
 *
 * Accepts `className` for sizing (e.g. `className="size-12"`).
 * The green colour (#36B37E) is baked in to match the design.
 */
export const BalanceIcon: React.FC<IconProps> = ({ className, ...rest }) => (
  <svg
    viewBox="0 0 96 96"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    {...rest}>
    <rect x="12" y="12" width="72" height="72" rx="36" fill="#36B37E" fillOpacity="0.17" />
    <rect
      x="6"
      y="6"
      width="84"
      height="84"
      rx="42"
      stroke="#36B37E"
      strokeOpacity="0.08"
      strokeWidth="12"
    />
    <rect x="34" y="36" width="30" height="15" rx="1" stroke="#36B37E" strokeWidth="1.5" />
    <path d="M34 55.5H64" stroke="#36B37E" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M34 60H64" stroke="#36B37E" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="49" cy="43.5" r="3" stroke="#36B37E" strokeWidth="1.5" />
    <path
      d="M40.75 43.5C40.75 43.9142 40.4142 44.25 40 44.25C39.5858 44.25 39.25 43.9142 39.25 43.5C39.25 43.0858 39.5858 42.75 40 42.75C40.4142 42.75 40.75 43.0858 40.75 43.5Z"
      fill="#36B37E"
      stroke="#36B37E"
      strokeWidth="1.5"
    />
    <path
      d="M58.75 43.5C58.75 43.9142 58.4142 44.25 58 44.25C57.5858 44.25 57.25 43.9142 57.25 43.5C57.25 43.0858 57.5858 42.75 58 42.75C58.4142 42.75 58.75 43.0858 58.75 43.5Z"
      fill="#36B37E"
      stroke="#36B37E"
      strokeWidth="1.5"
    />
  </svg>
);

export const TransactionIcon: React.FC<IconProps> = props => {
  const iconProps = getIconProps(props);
  return <Banknote {...iconProps} />;
};
